import styled from '@emotion/styled';
import { Bone } from '@lightspeed/flame/Bone';
import { Card } from '@lightspeed/flame/Card';
import { flameTheme, Flex } from '@lightspeed/flame/Core';
import { height, minWidth, space, width } from 'styled-system';

const MIN_BANNER_HEIGHT = 120;
const MAX_BANNER_WIDTH = 1056;

// Brand "discount green" from the back-book Figma frame (node 1545:8660). The nearest
// Flame token (green-800 = #059118) is visibly lighter, so we use the exact hex for the
// highlighted "% off" in the title.
const BACKBOOK_DISCOUNT_GREEN = '#047F31';
// Brand blue for the primary banner CTA from the back-book Figma frame. It sits
// between Flame's blue-400 (#537DEC) and blue-500 (#2E61DE) with no exact token, so
// we use the exact hex (also reused for the focus ring to keep the blue consistent).
const BACKBOOK_CTA_BLUE = '#3B6CE7';
const TABLET_MIN = flameTheme.breakpoints[0]; // 601px
const DESKTOP_MIN = flameTheme.breakpoints[1]; // 1024px

export const CapitalBannerCard = styled(Card)`
  background-color: #f2f5f8;
  border-color: #d7dce1;
  margin-bottom: 44px; // account for the -20px margin for page content
  min-height: ${MIN_BANNER_HEIGHT}px;
  max-width: ${MAX_BANNER_WIDTH}px;
  ${height};
  ${minWidth};
`;

export const CapitalImage = styled.img`
  ${height};
  ${space};
  ${width};
`;

export const CapitalBannerBone = styled(Bone)`
  ${space};
`;

export const CapitalContainer = styled(Flex)`
  min-height: ${MIN_BANNER_HEIGHT}px;
`;

/* ---------- Back-book banner (Figma node 1545:8660, light variant) ---------- */

// Responsive heights mirror the design: content-driven height on mobile (padded),
// 188px on tablet, 120px on desktop.
export const BackbookCard = styled(Card)`
  overflow: hidden;
  background-color: #f2f5f8;
  border-color: #d7dce1;
  border-radius: ${flameTheme.radii['radius-2']};
  min-height: ${MIN_BANNER_HEIGHT}px;
  max-width: ${MAX_BANNER_WIDTH}px;
  margin-bottom: 44px; // account for the -20px margin for page content

  @media (min-width: ${TABLET_MIN}) {
    height: 188px;
  }

  @media (min-width: ${DESKTOP_MIN}) {
    height: 120px;
  }
`;

// Fills the card height so the content can vertically centre against it (a plain
// height="100%" prop on Flame's Flex isn't reliably applied).
export const BackbookBody = styled(Flex)`
  width: 100%;
  height: 100%;
  min-height: inherit;
  align-items: stretch;
`;

// Composite lifestyle photo (discount badge baked into the asset). Hidden on mobile;
// fills the card height flush-left on tablet and desktop.
export const BackbookImage = styled.div`
  display: none;

  @media (min-width: ${TABLET_MIN}) {
    display: block;
    align-self: stretch;
    flex-shrink: 0;
  }

  & img {
    display: block;
    height: 100%;
    width: auto;
    min-width: 120px;
    object-fit: cover;
  }
`;

// Text + actions. Stacks vertically (text over buttons) on mobile/tablet, and lays
// out as a row (text | buttons) centered on desktop.
export const BackbookContent = styled(Flex)`
  flex: 1;
  min-width: 0;
  gap: 16px;
  padding: 16px;
  justify-content: center;

  @media (min-width: ${DESKTOP_MIN}) {
    align-items: center;
    gap: ${flameTheme.space[4]};
    padding: 0 ${flameTheme.space[4]};
  }
`;

export const BackbookExpiryBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${flameTheme.space[1]};
  flex-shrink: 0;
  padding: ${flameTheme.space[1]} ${flameTheme.space[2]};
  border-radius: 12px;
  background-color: ${flameTheme.colors['orange-100']};
  color: ${flameTheme.colors.textHeading};
  font-size: ${flameTheme.fontSizes['text-s']};
  font-weight: ${flameTheme.fontWeights.bold};
  line-height: ${flameTheme.lineHeights[3]};
  white-space: nowrap;
`;

export const BackbookHighlightedPercent = styled.span`
  color: ${BACKBOOK_DISCOUNT_GREEN};
  font-weight: ${flameTheme.fontWeights.bold};
`;

// Buttons sit side-by-side (equal width) on mobile/tablet and stack into a fixed
// column on desktop.
export const BackbookButtons = styled(Flex)`
  flex-shrink: 0;
  gap: ${flameTheme.space[2]};

  & > button {
    flex: 1 1 0;
  }

  @media (min-width: ${DESKTOP_MIN}) {
    flex-direction: column;
    width: 180px;

    & > button {
      flex: 0 0 auto;
      width: 100%;
    }
  }
`;

// Custom button instead of Flame's Button: the design needs the label left-aligned
// with the icon right-aligned (space-between), which Flame's Button (centered
// content) cannot express. Secondary = solid brand-blue CTA; default = white with a
// light border so it reads on the light card.
export const BannerButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  height: 36px;
  padding: 8px ${flameTheme.space[2]};
  border-radius: 4px;
  font-family: inherit;
  font-size: ${flameTheme.fontSizes['text-s']};
  font-weight: ${flameTheme.fontWeights.bold};
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  background-color: ${({ variant }) =>
    variant === 'secondary' ? BACKBOOK_CTA_BLUE : flameTheme.colors.white};
  color: ${({ variant }) =>
    variant === 'secondary' ? flameTheme.colors.white : flameTheme.colors['gray-800']};
  border: 1px solid ${({ variant }) => (variant === 'secondary' ? 'transparent' : '#d7dce1')};

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px solid ${BACKBOOK_CTA_BLUE};
    outline-offset: 2px;
  }

  & svg {
    margin-left: ${flameTheme.space[2]};
    flex-shrink: 0;
  }
`;
